import { Link, useParams } from 'react-router-dom';
import { useMyPlatforms, useProfilePerks } from '../hooks/use-platforms';
import {
  formatDate,
  formatQuota,
  formatRenewInterval,
  formatTimeLeft,
  grantPolicyLabel,
  statusLabel,
} from '../labels';
import type { MyPerk } from '../../domain/entities/platform.entity';

/**
 * "Ver meus acessos" de UM perfil: destino próprio, não acordeão no card — é
 * conteúdo que a pessoa vai querer reler, linkável e com volta pelo botão do
 * navegador.
 *
 * Mostra também o que **não** está ativo (expirado, esgotado, suspenso), porque
 * um perk só ativo nunca conta a história da expiração. Revogado o back não
 * manda: é terminal e é assunto de suporte, com contexto.
 */
export function PlatformPerksPage() {
  const { platformProfileId } = useParams<{ platformProfileId: string }>();
  const {
    data: perks,
    isLoading,
    isError,
  } = useProfilePerks(platformProfileId);
  const { data: platforms } = useMyPlatforms();

  // A lista de plataformas normalmente já está em cache (a pessoa chegou aqui
  // pelo card). Sem ela, o título cai no genérico em vez de mentir um nome.
  const platform = platforms?.find(
    (p) => p.platformProfileId === platformProfileId,
  );

  return (
    <div className="max-w-240 mx-auto space-y-6">
      <div>
        <Link
          to="/platforms"
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-sm font-medium transition-colors"
        >
          ← Minhas plataformas
        </Link>
        <h1 className="text-(--color-text-primary) mt-2 text-2xl font-bold">
          {platform ? platform.platformName : 'Meus acessos'}
        </h1>
        <p className="text-(--color-text-secondary) mt-1 text-sm">
          O que você tem nesta plataforma, quanto resta e até quando vale
        </p>
      </div>

      {isLoading && (
        <div className="space-y-4" aria-hidden>
          {[0, 1].map((i) => (
            <div
              key={i}
              className="border-(--color-border) bg-(--color-bg-primary) h-32 animate-pulse rounded-xl border"
            />
          ))}
        </div>
      )}

      {isError && (
        <p className="text-(--color-text-secondary) text-sm">
          Não foi possível carregar seus acessos agora.
        </p>
      )}

      {perks && perks.length === 0 && (
        <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-6">
          <p className="text-(--color-text-primary) text-sm font-medium">
            Nenhum acesso registrado nesta plataforma.
          </p>
        </div>
      )}

      {perks && perks.length > 0 && (
        <div className="space-y-4">
          {/*
            Índice na key porque o DTO não carrega id — de propósito: crédito
            abre pacotes paralelos, então nem `perkKey` nem `expiresAt`
            identificam uma linha sozinhos, e a lista é estática dentro da
            resposta.
          */}
          {perks.map((perk, index) => (
            <PerkCard key={`${perk.perkKey}-${index}`} perk={perk} />
          ))}
        </div>
      )}
    </div>
  );
}

function PerkCard({ perk }: { perk: MyPerk }) {
  const status = statusLabel(perk.status);
  const quota = formatQuota(perk);
  const expiresAt = formatDate(perk.expiresAt);
  const timeLeft = formatTimeLeft(perk.expiresAt);
  const renewAt = formatDate(perk.renewAt);
  const renewInterval = formatRenewInterval(perk.renewEveryInMilliseconds);

  return (
    <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {/*
            O título é a `perkKey` — identificador técnico. `Perk` não tem nome
            amigável no catálogo hoje, e inventar um mapa de labels aqui criaria
            uma segunda fonte de verdade que desatualiza no primeiro perk novo.
          */}
          <h2 className="text-(--color-text-primary) font-mono text-sm font-semibold">
            {perk.perkKey}
          </h2>
          <p className="text-(--color-text-secondary) mt-0.5 text-sm">
            {grantPolicyLabel(perk.grantPolicyKind)}
          </p>
        </div>
        <StatusBadge label={status.label} tone={status.tone} />
      </div>

      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        {/* Crédito mostra saldo; janela não tem contador e não mostra nada. */}
        {quota && <Field term="Uso" description={quota} />}

        {expiresAt && (
          <Field
            term="Válido até"
            description={timeLeft ? `${expiresAt} · ${timeLeft}` : expiresAt}
          />
        )}

        {renewAt && (
          <Field
            term="Renovação"
            description={
              renewInterval ? `${renewAt} · ${renewInterval}` : renewAt
            }
          />
        )}
      </dl>

      {/*
        Derivado não tem prazo próprio: vale enquanto o acesso que o inclui
        valer. O selo ocupa o lugar do "Válido até" — é a mesma pergunta ("até
        quando?"), respondida sem inventar uma data. Viaja com o card, não com
        a lista: a linha se explica sozinha.
      */}
      {perk.derived && (
        <p className="text-(--color-text-secondary) text-sm">
          Incluído no seu{' '}
          {perk.derivedFromKeys.map((key, index) => (
            <span key={key}>
              {index > 0 &&
                (index === perk.derivedFromKeys.length - 1 ? ' e ' : ', ')}
              <span className="text-(--color-text-primary) font-mono">
                {key}
              </span>
            </span>
          ))}
        </p>
      )}

      {perk.statusReason && (
        <p className="text-(--color-text-secondary) border-(--color-border) mt-4 border-t pt-3 text-sm">
          {perk.statusReason}
        </p>
      )}
    </div>
  );
}

function Field({ term, description }: { term: string; description: string }) {
  return (
    <div>
      <dt className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-wide">
        {term}
      </dt>
      <dd className="text-(--color-text-primary) mt-0.5 text-sm">
        {description}
      </dd>
    </div>
  );
}

const TONE_CLASSES: Record<
  'success' | 'warning' | 'error' | 'default',
  string
> = {
  success:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  warning:
    'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
  default: 'bg-(--color-bg-secondary) text-(--color-text-secondary)',
};

function StatusBadge({
  label,
  tone,
}: {
  label: string;
  tone: 'success' | 'warning' | 'error' | 'default';
}) {
  return (
    <span
      className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASSES[tone]}`}
    >
      {label}
    </span>
  );
}
